import React, {useCallback, useEffect, useRef, useState} from 'react';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Input from '@material-ui/core/Input';
import {FormControl} from '@material-ui/core';

import useInterval from '~/customHooks/useInterval';
import Text from '~/components/ui/Text';

const useStyles = makeStyles(
  () => ({
    input: {
      padding: '4px 12px 4px 15px',
      border: 'solid 1px #808080',
      borderRadius: 10,
      fontSize: 18,
    },
    inputFocused: {
      borderColor: '#292929',
    },
  }),
  {name: 'PhoneVerificationForm'}
);

interface Props {
  onClickChangeNumber?: () => void;
  onSubmit: (codeString: string, formRef: null | HTMLFormElement) => void;
  buttonTitle?: string;
  resendCode: (isInitial?: boolean) => void;
  loading?: boolean;
  showResend?: boolean;
}

const PhoneVerificationForm: React.FC<Props> = ({
  onClickChangeNumber,
  onSubmit,
  buttonTitle = 'Verify',
  resendCode,
  loading = false,
  showResend = true,
}) => {
  const [phoneCode, setPhoneCode] = useState('');
  const [timeToNextSent, setTimeToNextSent] = useState<null | number>(null);
  const [tries, setTries] = useState(0);
  const [wrongCode, setWrongCode] = useState(false);
  const s = useStyles();
  const formRef = useRef<HTMLFormElement | null>(null);

  const [stopInterval, startInterval] = useInterval(() => {
    if (timeToNextSent) {
      setTimeToNextSent(timeToNextSent - 1);
    }
  }, 1000);

  useEffect(() => {
    if (!timeToNextSent || timeToNextSent < 0) {
      stopInterval();
    }
  }, [stopInterval, timeToNextSent]);

  const resendCodeDebounce = useCallback(() => {
    setWrongCode(false);

    setTimeToNextSent(30);
    startInterval();
    resendCode();
  }, [resendCode, startInterval]);

  const onChange = useCallback(({target: {value: inputValue}}) => {
    setWrongCode(false);
    if (inputValue.length <= 4) {
      setPhoneCode(inputValue);
    }
  }, []);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (!phoneCode) {
        return;
      }

      setTries(tries + 1);
      await onSubmit(phoneCode, formRef && formRef.current);
      setPhoneCode('');
      setWrongCode(true);
    },
    [phoneCode, tries, onSubmit]
  );

  return (
    <form onSubmit={handleSubmit} ref={formRef} className="test-form-verification-phone">
      <FormControl required fullWidth>
        <Box mb={1.5}>
          <Input
            autoComplete="one-time-code"
            autoFocus
            disableUnderline
            fullWidth
            className={s.input}
            classes={{focused: s.inputFocused}}
            name="phone-input"
            type="number"
            placeholder="Enter 4 digit code"
            onChange={onChange}
            value={phoneCode}
          />
        </Box>
        {wrongCode && (
          <Box mb={1.5}>
            <Text fs="14" color="primary">
              You have entered a wrong code
            </Text>
          </Box>
        )}
        {showResend && (
          <Box mb={1.5}>
            {timeToNextSent ? (
              `Please wait ${timeToNextSent} seconds before resending the verification code again.`
            ) : (
              <Button color="default" variant="text" onClick={resendCodeDebounce}>
                <Text fs="16" color="gray">
                  I did not get a code
                </Text>
              </Button>
            )}
          </Box>
        )}
        {onClickChangeNumber && (
          <Box mb={1.5}>
            <Button color="default" variant="text" onClick={onClickChangeNumber}>
              <Text fs="16" color="gray">
                Change phone number
              </Text>
            </Button>
          </Box>
        )}
        <Button type="submit" disabled={!phoneCode || loading}>
          {buttonTitle}
        </Button>
      </FormControl>
    </form>
  );
};

export default PhoneVerificationForm;
